import { searchAndShowProductImages, resolveAttachmentReferences, stripExampleReferences } from '@frontend/store/creative-studio/chat-function-handlers/search-products';

export const handleLayoutCreationResponse = async ($message, { state, commit, dispatch } = {}) => {
  let args = {};
  try {
    args = typeof $message.function_call.arguments === 'string'
      ? JSON.parse($message.function_call.arguments)
      : $message.function_call.arguments || {};
  } catch (e) {
    // ignore parse errors
  }

  const productReferences = resolveAttachmentReferences(
    // Drop refs the model pulled out of "e.g." example text (POR-13481).
    stripExampleReferences(args.product_references || [], args.image_prompt),
    $message.savedAttachments,
    state?.messages || []
  );

  if (args.image_prompt && commit) {
    commit('SET_IMAGE_PROMPT', args.image_prompt);
  }

  if ((args.background_scene || productReferences.length || args.text_elements) && commit) {
    commit('SET_PARSED_PROMPT', {
      ...(state.parsedPrompt || {}),
      background_scene: args.background_scene || null,
      product_references: productReferences,
      text_elements: args.text_elements || [],
    });
    await dispatch('saveConversationState');
  }

  // If the backend already created the layout, redirect to it
  if ($message.function_call.data?.layoutURL) {
    window.location.href = $message.function_call.data.layoutURL;
    return;
  }

  // Otherwise, create the layout with custom sizes
  const sizes = args.sizes;
  if (!sizes || !sizes.length) {
    return;
  }

  // Deterministic guard (POR-13481): create_template uses 1024x1024 as its default
  // when it can't map the request to a configured network or an explicit size. For a
  // banner/generation request that falls back to that default WITHOUT the user giving
  // pixel sizes, don't silently auto-create a generic square layout — it means no
  // configured network matched (e.g. the user asked for an "A+ Banner" but Amazon isn't
  // set up). Tell the user instead of redirecting them to a wrong-sized empty layout.
  const isGenerationRequest = !!(args.image_prompt || args.background_scene);
  const isDefaultSquareFallback = sizes.length === 1
    && Number(sizes[0].width) === 1024
    && Number(sizes[0].height) === 1024;
  const requestText = (
    ([...(state?.messages || [])].reverse().find((m) => m.role === 'user')?.content || '')
    + ' ' + (args.image_prompt || '')
  );
  const userSpecifiedSize = /\d{2,4}\s*[x×]\s*\d{2,4}/i.test(requestText);

  if (isGenerationRequest && isDefaultSquareFallback && !userSpecifiedSize && commit) {
    const available = (state.allNetworks || []).map((n) => n.name).filter(Boolean);
    const availableLine = available.length
      ? ` Your available networks are: <b>${available.join('</b>, <b>')}</b>.`
      : '';
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: `I couldn't match this to one of your configured networks, so I'm not sure what banner sizes to use.${availableLine} To create this on a specific network (e.g. Amazon A+), please ask your admin to set it up — or tell me the exact sizes you'd like (e.g. <b>970x600</b>).`,
    });
    return;
  }

  // Search for product images before creating layout
  if (productReferences.length > 0 && commit) {
    await searchAndShowProductImages(productReferences, { commit, dispatch });
  }

  if (commit) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: `Creating layout with ${sizes.length} template${sizes.length > 1 ? 's' : ''} (${sizes.map(s => s.width + 'x' + s.height).join(', ')})...`,
    });
  }

  try {
    const payload = {
      sizes,
      image_prompt: args.image_prompt || null,
      background_scene: args.background_scene || null,
      product_references: productReferences,
      text_elements: args.text_elements || [],
      layout_mode: state?.layoutMode || 'auto',
    };

    const { data } = await axios.post('/networks/layout-custom-sizes', payload);

    if (data?.link) {
      window.location.href = data.link;
    }
  } catch (e) {
    console.error('Failed to create layout with custom sizes:', e);
    if (commit) {
      await dispatch('addAndSaveMessage', {
        role: 'assistant',
        content: 'Failed to create the layout. Please try again.',
      });
    }
  }
};
